import { useEffect, useContext, useState } from 'react'
import type { NextPageContext } from 'next'
import { useRouter } from 'next/router'
import { inject, observer } from 'mobx-react'
import styled from 'styled-components'
import queryString from 'query-string'

import { SESSION_EXPIRED, HTTP_CODES } from 'utils/constants'

import loadMenu from 'utils/api/loadMenu/loadMenu'
import { getStore, checkRequest, isMobile } from 'utils/utils'
import { CACHE_POLICY } from '../utils/constants'

import MenuDesktop from 'pages/MenuDesktop'
import MenuMobile from 'pages/MenuMobile'
import { StoreContext } from 'contexts/StoreContext'
import { CustomError } from 'components/errors/CustomError'
import LoadingSpinner from 'components/common/LoadingSpinner'

import { useStores } from 'hooks/useStores'

const LoadingSpinnerContainer = styled.div`
	width: 100vw;
	height: 100vh;
	display: flex;
	flex-flow: row nowrap;
	justify-content: center;
	align-items: center;
`

const Menu = inject(
	'Infra',
	'User',
	'Application',
	'Session'
)(
	observer(({ Infra, User, Application, Session }: any) => {
		const router = useRouter()

		// TODO FOR NEXT: check if after migration to app directory, we can use setStore here
		const { store, setStore } = useContext(StoreContext)
		const { addressManagerV2Store } = useStores()
		const [isMenuReady, setIsMenuReady] = useState(false)

		useEffect(() => {
			const queryParams = new URLSearchParams(window.location.search)
			const cust = queryParams.get('cust')
			const request = queryParams.get('request')
			const j = queryParams.get('j')
			const ot = queryParams.get('ot')
			const ref = queryParams.get('ref')
			const pc = queryParams.get('pc')

			// TODO FOR NEXT: review this part concerning the cache
			// Overload the appParams with the query params, so it avoids appParams to keep the server cache values
			// with the data of the fist server side rendering of the page, before cloudfront cache occurs instead SSR
			Infra.setAppParams({
				...Infra.appParams,
				cust,
				request,
				// j,
				ot,
			})

			const init = async () => {
				// Check session expiration on client side
				let serverSession: any = null
				try {
					serverSession = await checkRequest({
						request,
						cust,
						j,
					})
				} catch (error) {
					// If the session is expired, we redirect to home page
					if (error instanceof CustomError && error.type === HTTP_CODES.SESSION_EXPIRED) {

						// on new localization flow, we refresh the session from here instead of redirecting to home page
						// since when we navigate to the localized menu, we don't apply any pre-processing logic
						if(addressManagerV2Store.newLocalizationFlowEnabled) {
							const refObject = ref ? JSON.parse(decodeURIComponent(ref)) : {}
							const menuUrl = await Session.initOrUpdateSession({ refObject, storeId: j, chainId: pc, stopLoading: false })
							const [, menuUrlQuery] = (menuUrl ?? '').split('?')
							const newMenuQueryParams = queryString.parse(menuUrlQuery)

							serverSession = await checkRequest({
								request: newMenuQueryParams.request,
								cust: newMenuQueryParams.cust,
								j: newMenuQueryParams.j,
							})

							localStorage.setItem('menuUrl', `?${menuUrlQuery.split('#')[0]}`)
							router.replace(menuUrl)
						}

						// on old localization flow, it's done through AddressManager.onMenuClickWhenLocalized
						// so keep the redirection to home page
						else {
							sessionStorage.setItem(SESSION_EXPIRED, 'true')
							router.push('/')
							return
						}
					}
				}

				// Hydrate user session for web HAS locale
				User.setSession(serverSession)

				const data =
					store.data ??
					(await loadMenu({
						chainId: Infra.appParams.c,
						storeId: j ?? '',
						orderTypeFromQS: ot ?? 'all',
						appId: serverSession.appid,
						stopLoading: false,
					}))

				const metaData =
					store.metaData ??
					(await getStore({
						request: serverSession.request,
						cust,
					}, addressManagerV2Store))

				setStore({ ...store, data, metaData })

				setIsMenuReady(true)

				// Hydrate storage instance on client side, because of localstorage use
				// localStorage.setItem('storeId', data?.id ?? '')
			}

			init()
		}, [])

		if (!isMenuReady) {
			return (
				<LoadingSpinnerContainer>
					<LoadingSpinner chainId={Infra.appParams?.c} brand={Infra.appParams.brand} />
				</LoadingSpinnerContainer>
			)
		}

		if (isMobile() || Application.isMobileApp) {
			return <MenuMobile />
		}

		return <MenuDesktop />
	})
)

export async function getServerSideProps(
	context: NextPageContext & { req: { extra: { appParams: any; nextJsError?: string; forwardedUrl: string } } }
) {
	if (context.res && !context.req.extra.nextJsError) {
		context.res.setHeader(
			'Cache-Control',
			`public, s-maxage=${CACHE_POLICY.DEFAULT.MAX_AGE}, stale-while-revalidate=${CACHE_POLICY.DEFAULT.REVALIDATED_AGE}`
		)
	}

	return {
		props: {},
	}
}

export default Menu
